import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft, Loader2 } from "lucide-react";
import { UNITS, getUnit, setUnit, lbToKg, kgToLb, inToCm, cmToIn, inchesToFtIn, ftInToInches } from "@/lib/units";

const steps = [
  {
    key: "age",
    label: "How old are you?",
    type: "number",
    suffix: "years",
    sub: "Age dictates recovery windows and intensity.",
  },
  {
    key: "sex",
    label: "Biological sex",
    type: "choice",
    options: ["male", "female", "other"],
  },
  {
    key: "height_cm",
    label: "Height",
    type: "height",
  },
  {
    key: "weight_kg",
    label: "Weight",
    type: "weight",
  },
  {
    key: "goal",
    label: "Primary goal",
    type: "choice",
    options: [
      { v: "fat_loss", l: "Fat Loss" },
      { v: "muscle_gain", l: "Muscle Gain" },
      { v: "strength", l: "Raw Strength" },
      { v: "endurance", l: "Endurance" },
      { v: "recomp", l: "Body Recomposition" },
    ],
  },
  {
    key: "experience",
    label: "Training experience",
    type: "choice",
    options: [
      { v: "beginner", l: "Beginner (<1yr)" },
      { v: "intermediate", l: "Intermediate (1-3yr)" },
      { v: "advanced", l: "Advanced (3yr+)" },
    ],
  },
  {
    key: "days_per_week",
    label: "Training days per week",
    type: "choice",
    options: [3, 4, 5, 6],
  },
  {
    key: "equipment",
    label: "Equipment access",
    type: "choice",
    options: [
      { v: "full_gym", l: "Full Gym" },
      { v: "home_minimal", l: "Home (DBs / Bands)" },
      { v: "bodyweight", l: "Bodyweight Only" },
    ],
  },
  {
    key: "diet_preference",
    label: "Diet preference",
    type: "choice",
    options: [
      { v: "omnivore", l: "Omnivore" },
      { v: "vegetarian", l: "Vegetarian" },
      { v: "vegan", l: "Vegan" },
      { v: "keto", l: "Keto" },
      { v: "paleo", l: "Paleo" },
    ],
  },
  {
    key: "allergies",
    label: "Allergies or intolerances?",
    type: "text",
    placeholder: "e.g. peanuts, dairy (or leave blank)",
    optional: true,
  },
  {
    key: "injuries",
    label: "Injuries or limitations?",
    type: "text",
    placeholder: "e.g. lower back, left knee (or leave blank)",
    optional: true,
  },
];

export default function Questionnaire() {
  const [i, setI] = useState(0);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const step = steps[i];
  const progress = ((i + 1) / steps.length) * 100;

  const set = (v) => setAnswers((a) => ({ ...a, [step.key]: v }));

  const next = () => {
    if (!step.optional && (answers[step.key] === undefined || answers[step.key] === "")) {
      toast.error("Please answer to continue.");
      return;
    }
    if (i < steps.length - 1) setI(i + 1);
    else submit();
  };

  const submit = async () => {
    setLoading(true);
    try {
      const payload = {
        age: Number(answers.age),
        sex: answers.sex,
        height_cm: Number(answers.height_cm),
        weight_kg: Number(answers.weight_kg),
        goal: answers.goal,
        experience: answers.experience,
        days_per_week: Number(answers.days_per_week),
        equipment: answers.equipment,
        diet_preference: answers.diet_preference,
        allergies: answers.allergies || "",
        injuries: answers.injuries || "",
        notes: "",
      };
      const { data } = await api.post("/plans/generate", payload);
      toast.success("Your plan is forged.");
      navigate(`/plan/${data.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "AI failed. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] flex flex-col" data-testid="questionnaire-page">      <div className="fixed top-16 left-0 right-0 h-0.5 bg-white/5 z-40">
        <div
          className="h-full bg-gradient-to-r from-[#FF3333] to-[#00E5FF] transition-all duration-300 shadow-[0_0_10px_rgba(0,229,255,0.6)]"
          style={{ width: `${progress}%` }}
          data-testid="questionnaire-progress"
        />
      </div>

      <div className="max-w-2xl mx-auto w-full px-6 py-16 md:py-24 flex-1">
        <div className="font-mono text-[10px] tracking-[0.4em] text-[#00E5FF] mb-3">
          STEP {String(i + 1).padStart(2, "0")} / {String(steps.length).padStart(2, "0")} · FORGE-AI
        </div>

        <h1 className="font-heading uppercase text-4xl md:text-5xl leading-tight" data-testid="questionnaire-label">
          {step.label}
        </h1>
        {step.sub && (
          <p className="font-sub text-white/50 mt-2">{step.sub}</p>
        )}

        <div className="mt-10">
          {step.type === "number" && (
            <div className="flex items-end gap-3">
              <input
                type="number"
                step={step.step || "1"}
                autoFocus
                value={answers[step.key] ?? ""}
                onChange={(e) => set(e.target.value)}
                data-testid={`q-input-${step.key}`}
                className="w-48 bg-transparent border-0 border-b-2 border-white/20 focus:border-[#FF3333] outline-none py-2 font-heading text-5xl"
              />
              <span className="font-mono text-sm text-white/40 mb-3">{step.suffix}</span>
            </div>
          )}
          {step.type === "text" && (
            <input
              type="text"
              autoFocus
              placeholder={step.placeholder}
              value={answers[step.key] ?? ""}
              onChange={(e) => set(e.target.value)}
              data-testid={`q-input-${step.key}`}
              className="w-full bg-transparent border-0 border-b-2 border-white/20 focus:border-[#00E5FF] outline-none py-2 font-sub text-xl"
            />
          )}
          {step.type === "weight" && <WeightInput value={answers.weight_kg} onChange={(kg) => set(kg)} />}
          {step.type === "height" && <HeightInput value={answers.height_cm} onChange={(cm) => set(cm)} />}
          {step.type === "choice" && (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {step.options.map((opt) => {
                const v = typeof opt === "object" ? opt.v : opt;
                const l = typeof opt === "object" ? opt.l : String(opt);
                const selected = answers[step.key] === v;
                return (
                  <button
                    key={v}
                    type="button"
                    onClick={() => set(v)}
                    data-testid={`q-choice-${step.key}-${v}`}
                    className={`p-4 border text-left font-sub uppercase tracking-wide transition-all ${
                      selected
                        ? "border-[#FF3333] bg-[#FF3333]/5 text-white shadow-[0_0_20px_rgba(255,51,51,0.2)]"
                        : "border-white/15 text-white/70 hover:border-white/40 hover:text-white"
                    }`}
                  >
                    <div className="text-base">{l}</div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div className="mt-16 flex items-center justify-between">
          <button
            onClick={() => setI(Math.max(0, i - 1))}
            disabled={i === 0}
            data-testid="q-back"
            className="font-mono text-xs tracking-widest text-white/50 hover:text-white disabled:opacity-30 flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" /> BACK
          </button>
          <button
            onClick={next}
            disabled={loading}
            data-testid="q-next"
            className="btn-forged-red disabled:opacity-50"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" /> Forging your plan...
              </>
            ) : i === steps.length - 1 ? (
              <>Generate Plan <ArrowRight className="w-4 h-4" /></>
            ) : (
              <>Next <ArrowRight className="w-4 h-4" /></>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

function UnitToggle({ value, options, onChange }) {
  return (
    <div className="inline-flex border border-white/15 ml-3 mb-3">
      {options.map((o) => (
        <button
          key={o.v}
          type="button"
          onClick={() => onChange(o.v)}
          data-testid={`unit-toggle-${o.v}`}
          className={`px-3 py-1 font-mono text-[10px] tracking-widest transition-colors ${
            value === o.v ? "bg-[#FF3333] text-black" : "text-white/50 hover:text-white"
          }`}
        >
          {o.l}
        </button>
      ))}
    </div>
  );
}

function WeightInput({ value, onChange }) {
  const [unit, setU] = useState(() => getUnit(UNITS.WEIGHT, "lb"));
  // display value in current unit
  const display = value == null
    ? ""
    : unit === "lb"
    ? Math.round(kgToLb(value) * 10) / 10
    : Math.round(value * 10) / 10;

  const handleChange = (raw) => {
    if (raw === "") return onChange(undefined);
    const num = parseFloat(raw);
    if (Number.isNaN(num)) return;
    onChange(unit === "lb" ? lbToKg(num) : num);
  };

  const switchUnit = (newUnit) => {
    setU(newUnit);
    setUnit(UNITS.WEIGHT, newUnit);
  };

  return (
    <div>
      <UnitToggle
        value={unit}
        options={[{ v: "lb", l: "LB" }, { v: "kg", l: "KG" }]}
        onChange={switchUnit}
      />
      <div className="flex items-end gap-3">
        <input
          type="number"
          step="0.1"
          autoFocus
          value={display}
          onChange={(e) => handleChange(e.target.value)}
          data-testid="q-input-weight"
          className="w-48 bg-transparent border-0 border-b-2 border-white/20 focus:border-[#FF3333] outline-none py-2 font-heading text-5xl"
        />
        <span className="font-mono text-sm text-white/40 mb-3">{unit}</span>
      </div>
    </div>
  );
}

function HeightInput({ value, onChange }) {
  const [unit, setU] = useState(() => getUnit(UNITS.HEIGHT, "in"));
  // For in mode: edit feet + inches. For cm mode: single field.
  const totalInches = value == null ? 0 : cmToIn(value);
  const { ft, in: inches } = inchesToFtIn(totalInches);

  const handleCm = (raw) => {
    if (raw === "") return onChange(undefined);
    const num = parseFloat(raw);
    if (!Number.isNaN(num)) onChange(num);
  };
  const handleFt = (raw) => {
    const num = parseFloat(raw) || 0;
    onChange(inToCm(ftInToInches(num, inches)));
  };
  const handleIn = (raw) => {
    const num = parseFloat(raw) || 0;
    onChange(inToCm(ftInToInches(ft, num)));
  };
  const switchUnit = (newUnit) => {
    setU(newUnit);
    setUnit(UNITS.HEIGHT, newUnit);
  };

  return (
    <div>
      <UnitToggle
        value={unit}
        options={[{ v: "in", l: "FT/IN" }, { v: "cm", l: "CM" }]}
        onChange={switchUnit}
      />
      {unit === "cm" ? (
        <div className="flex items-end gap-3">
          <input
            type="number"
            step="0.5"
            autoFocus
            value={value == null ? "" : Math.round(value * 10) / 10}
            onChange={(e) => handleCm(e.target.value)}
            data-testid="q-input-height-cm"
            className="w-48 bg-transparent border-0 border-b-2 border-white/20 focus:border-[#FF3333] outline-none py-2 font-heading text-5xl"
          />
          <span className="font-mono text-sm text-white/40 mb-3">cm</span>
        </div>
      ) : (
        <div className="flex items-end gap-3">
          <div className="flex items-end gap-2">
            <input
              type="number"
              autoFocus
              value={value == null ? "" : ft}
              onChange={(e) => handleFt(e.target.value)}
              data-testid="q-input-height-ft"
              className="w-28 bg-transparent border-0 border-b-2 border-white/20 focus:border-[#FF3333] outline-none py-2 font-heading text-5xl"
            />
            <span className="font-mono text-sm text-white/40 mb-3">ft</span>
          </div>
          <div className="flex items-end gap-2">
            <input
              type="number"
              step="0.5"
              value={value == null ? "" : inches}
              onChange={(e) => handleIn(e.target.value)}
              data-testid="q-input-height-in"
              className="w-28 bg-transparent border-0 border-b-2 border-white/20 focus:border-[#FF3333] outline-none py-2 font-heading text-5xl"
            />
            <span className="font-mono text-sm text-white/40 mb-3">in</span>
          </div>
        </div>
      )}
    </div>
  );
}

